import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { Battery, Sun, Shield, Smartphone, Zap, Clock } from "lucide-react";
import { SEO } from "@/components/seo/SEO";

const Thuisbatterijen = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <SEO
          title="Thuisbatterij Prijs | Energie Opslaan & Rendement | SOL-VI"
          description="🔋 Thuisbatterij vanaf €199/maand 💰 Tot €6.240/jaar verdienen 🏠 Ook voor huurders & verhuurders ⚡ 24/7 eigen stroom 📞 Gratis advies Gelderland"
          keywords="thuisbatterij prijs, thuisbatterij kosten, batterij zonnepanelen, energie opslaan, thuisbatterij rendement, batterij voor huurders, thuisbatterij Arnhem"
          canonical="https://sol-vi.nl/thuisbatterijen"
          type="website"
        />
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Thuisbatterij Prijs: Verdien Tot €6.240 per Jaar
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Sla energie op, verdien geld en word 100% energieonafhankelijk. Ook zonder zonnepanelen!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                  Vraag Gratis Offerte
                </Button>
                <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                  Plan Adviesgesprek
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Special offer section */}
              <div className="mb-16 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <img 
                      src="/lovable-uploads/036c5ca5-3137-4d09-9272-b9313f66cbd5.png" 
                      alt="Thuisbatterij rendement berekening: verdien €6.240 per jaar met batterij opslag voor huurders en verhuurders zonder zonnepanelen" 
                      className="w-full h-auto rounded-lg shadow-lg"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4">Voor Huurders & Verhuurders</h2>
                    <p className="text-lg mb-4">
                      <strong>Verdien tot €6.240,- per woning per jaar - zonder huurverhoging!</strong>
                    </p>
                    <p className="text-muted-foreground mb-6">
                      Geen zonnepanelen nodig - ook geschikt voor appartementen en monumentale panden! 
                      Onze slimme thusbatterijen worden gekoppeld aan eigen groene energieparken (€0.08/kWh ex. btw). 
                      Met ons algoritme optimaliseren we verbruik en verkoop op de energiemarkt.
                    </p>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <div className="bg-background/80 rounded-lg p-4">
                        <h4 className="font-semibold text-green-600 mb-2">Voor Huurders</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Batterijverbruik afgestemd op uw woning</li>
                          <li>• Tot €150-160 opbrengst per week</li>
                          <li>• Terugverdiend in ±1-1,5 jaar</li>
                        </ul>
                      </div>
                      <div className="bg-background/80 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-600 mb-2">Voor Verhuurders</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Gratis basisstroom (2.5 kWh/dag)</li>
                          <li>• €5.850 tot €6.240 per woning/jaar</li>
                          <li>• Terugverdiend in ±1,5-2 jaar</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Met zonnepanelen wekt u overdag volop energie op, maar wat gebeurt er 's avonds als de zon onder is? Met een thuisbatterij van SOL-VI slaat u de overtollige zonne-energie eenvoudig op. Zo gebruikt u 's avonds en 's nachts uw eigen, gratis stroom en wordt u maximaal onafhankelijk van het elektriciteitsnet.
                </p>
              </div>

              {/* How it works */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Hoe werkt een thuisbatterij?</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  De thuisbatterij wordt aangesloten op uw zonnepanelensysteem. Overdag, wanneer uw panelen meer stroom produceren dan u verbruikt, laadt de batterij automatisch op. Zodra de zon ondergaat of bij een stroomstoring, schakelt uw huis automatisch over op de opgeslagen energie uit de batterij. Slimme software zorgt ervoor dat dit proces volledig geautomatiseerd verloopt.
                </p>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">De voordelen van een SOL-VI thuisbatterij:</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">24/7 Eigen Stroom</h3>
                      <p className="text-muted-foreground">Gebruik uw zelf opgewekte zonne-energie dag en nacht en maximaliseer uw eigen verbruik.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Volledige Onafhankelijkheid</h3>
                      <p className="text-muted-foreground">Wees voorbereid op de afbouw van de salderingsregeling. Met een batterij bent u niet langer afhankelijk van het net.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Extra Besparingen</h3>
                      <p className="text-muted-foreground">Door minder stroom van het net te hoeven kopen, verlaagt u uw energierekening nog verder.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Battery className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Noodstroomvoorziening</h3>
                      <p className="text-muted-foreground">Sommige systemen kunnen bij een stroomstoring uw belangrijkste apparaten van stroom blijven voorzien.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Process */}
              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-8">Onze Werkwijze: Slimme Energieopslag</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Analyse & Advies</h3>
                      <p className="text-muted-foreground">We berekenen op basis van uw verbruik en de opbrengst van uw zonnepanelen de ideale capaciteit voor uw thuisbatterij.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Naadloze Integratie</h3>
                      <p className="text-muted-foreground">U ontvangt een offerte voor een batterijsysteem dat perfect samenwerkt met uw bestaande of nieuwe zonnepanelen.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Veilige Installatie</h3>
                      <p className="text-muted-foreground">Onze technici installeren de batterij op een veilige en geschikte locatie in uw woning.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">Slim Beheer</h3>
                      <p className="text-muted-foreground">We configureren het systeem en leggen u uit hoe u via een app uw energieopslag en verbruik kunt monitoren.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Final CTA */}
              <div className="text-center bg-muted/50 rounded-lg p-8">
                <h2 className="text-2xl font-bold mb-4">Klaar om de baas te worden over uw eigen energie?</h2>
                <p className="text-muted-foreground mb-6">Ontdek de mogelijkheden van energieopslag voor uw situatie.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="hero" size="lg" onClick={() => (window as any).renderOpenSolarWidget()}>
                    Vraag een vrijblijvende offerte aan
                  </Button>
                  <Button variant="outline" size="lg" onClick={() => (window as any).Calendly.initPopupWidget({url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800', pageSettings: {backgroundColor: 'ffffff', hideEventTypeDetails: false, hideLandingPageDetails: false, primaryColor: 'ff7800', textColor: '4d5055'}, prefill: {}, utm: {}})}>
                    Plan uw gratis adviesgesprek
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Thuisbatterijen;